<template>
  <p class="section__title">
    <i
      class="title__icon"
      :class="CONFIG_BY_DATA_HEALTH_CHECK_TYPE[dataHealthType].icon"
    />
    {{ $t(CONFIG_BY_DATA_HEALTH_CHECK_TYPE[dataHealthType].title) }}
  </p>
  <div
    v-for="(status, field) in dataHealthDetail"
    :key="field"
    class="section__field-container"
    @click="navigateToDataHealth(field)"
  >
    <div class="field-container__info field-container__name">
      <div class="name__category">
        {{
          getMemberCategoryTranslation(
            getDimensionFromDataHealthCheck(field).meta.category!,
            MemberType.DIMENSION
          )
        }}
      </div>
      <i class="name__icon icon-tiny-arrow" />
      <div class="name__dimension">
        <i
          :class="`dimension__icon icon-${getMemberFormatIcon(
            getDimensionFromDataHealthCheck(field)
          )}`"
        />
        <label class="dimension__label">
          {{ getDimensionTranslation(getDimensionFromDataHealthCheck(field)) }}
        </label>
      </div>
    </div>
    <p class="field-container__info field-container__status">
      <Loader
        v-if="!status || status === DataHealthCheckStatus.LOADING"
        class="field-container__status__loader"
      />
      <template
        v-else-if="status !== DataHealthCheckStatus.INAPPLICABLE_FILTERS"
      >
        <i
          :class="[
            'field-container__status__icon',
            status === DataHealthCheckStatus.SUCCESS
              ? ['icon-success', 'status-icon__success']
              : ['icon-error', 'status-icon']
          ]"
        />
        {{ $t(CONFIG_BY_DATA_HEALTH_CHECK[field][status]) }}
      </template>
    </p>
    <p class="field-container__info field-container__open-details">
      {{ $t('settings.dataHealth.module.infoModal.openDetails') }}
      <ReflectIconButton
        class="open-icon"
        icon="icon-open-external-2"
        size="small"
        theme="transparent"
      />
    </p>
  </div>
</template>

<script lang="ts">
import { mapState } from 'pinia'
import { defineComponent } from 'vue'
import {
  CONFIG_BY_DATA_HEALTH_CHECK,
  CONFIG_BY_DATA_HEALTH_CHECK_TYPE
} from '~/constants/dataQuality'
import {
  getDimensionFromTitle,
  getDimensionTranslation,
  getMemberCategoryTranslation,
  getMemberFormatIcon
} from '~/services/explore'
import { useDataQualityStore } from '~/stores/dataQuality'
import { useFiltersStore } from '~/stores/filters'
import { MemberType } from '~/types/analytics'
import {
  DataHealthCheckStatus,
  DataHealthCheck,
  DataHealthCheckType
} from '~/types/dataQuality'
import { QUERY_PARAMETERS } from '~/types/queryParameters'
import type { DateRange } from '~/types/timeDimension'

export default defineComponent({
  name: 'DataHealthModalSection',
  props: {
    dataHealthDetail: {
      type: Object as PropType<{
        [key in DataHealthCheck]?: DataHealthCheckStatus
      }>,
      required: true
    },
    dataHealthType: {
      type: String as PropType<DataHealthCheckType>,
      required: true
    },
    dateRange: {
      type: Object as PropType<DateRange>,
      required: true
    }
  },
  setup() {
    return {
      CONFIG_BY_DATA_HEALTH_CHECK_TYPE,
      CONFIG_BY_DATA_HEALTH_CHECK,
      DataHealthCheckStatus,
      MemberType,
      getMemberCategoryTranslation,
      getMemberFormatIcon,
      getDimensionTranslation
    }
  },
  computed: {
    ...mapState(useDataQualityStore, ['allDataHealthChecksMap']),
    ...mapState(useFiltersStore, ['allFilters'])
  },
  methods: {
    async navigateToDataHealth(check: DataHealthCheck) {
      await navigateTo(
        {
          path: CONFIG_BY_DATA_HEALTH_CHECK_TYPE[this.dataHealthType].route,
          query: {
            [QUERY_PARAMETERS.DATA_HEALTH_CHECK]: check,
            [QUERY_PARAMETERS.START_DATE]: this.dateRange[0],
            [QUERY_PARAMETERS.END_DATE]: this.dateRange[1],
            [QUERY_PARAMETERS.DASHBOARD_FILTERS]:
              this.allFilters.length > 0
                ? encodeURIComponent(JSON.stringify(this.allFilters))
                : undefined
          }
        },
        { open: { target: '_blank' } }
      )
    },
    getDimensionFromDataHealthCheck(dataHealthCheck: DataHealthCheck) {
      return getDimensionFromTitle(
        this.allDataHealthChecksMap[dataHealthCheck]!.dimension
      )
    }
  }
})
</script>

<style lang="scss" scoped>
.section {
  &__title {
    @include font-text($font-weight-medium);
    @include font-size($font-size-regular);
    padding: $margin-small 0 $margin-mini $margin-regular;
    background-color: $bg-quinary;
    border: 1px solid $border-quaternary;
    border-radius: 8px 8px 0 0;

    &__separator {
      width: 100%;
    }

    .title {
      &__icon {
        @include font-icon;
      }
    }
  }

  &__field-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    border: 1px solid $border-quaternary;
    border-top: none;

    .field-container {
      &__info {
        @include font-text($font-weight-book);
        @include font-size($font-size-mini);
        margin: $margin-small $margin-regular;
      }

      &__name {
        display: flex;
        align-items: center;
        overflow: hidden;
        text-overflow: ellipsis;
        flex-basis: 50%;

        .name {
          &__category {
            flex-shrink: 0;
          }

          &__icon {
            @include font-icon;
            margin: 0 $margin-small;
          }

          &__dimension {
            display: flex;
            align-items: center;

            .dimension {
              &__icon {
                @include font-icon;
                @include font-size($font-size-regular);
                color: $text-secondary;
                margin-right: $margin-tiny;
              }

              &__label {
                text-align: left;
                color: $text-secondary;
              }
            }
          }
        }

        &__check {
          &--fail {
            color: $bg-error;
          }

          &--success {
            color: $bg-success;
          }
        }

        &__arrow {
          @include font-icon;
          @include font-size(10px);
          margin: 0 $margin-regular;
        }
      }

      &__status {
        width: 240px;
        display: flex;
        align-items: center;
        flex: 2;

        &__loader {
          position: relative;
          top: 0;
          left: 0;
          margin: 0;
          width: 24px;
          height: 24px;
        }

        &__icon {
          @include font-icon;
          margin-right: $margin-tiny;
        }

        .status-icon {
          @include font-size($font-size-regular);
          color: $color-error;

          &__success {
            font-size: 6px;
            background-color: $color-palette-14;
            color: $bg-quinary;
            padding: 3px;
            border-radius: 50%;
            margin-right: $margin-mini;
            margin-left: 2px;
          }
        }
      }

      &__open-details {
        display: flex;
        align-items: center;

        .open-icon {
          border: none;
        }
      }
    }

    &:hover {
      background-color: $bg-new-quaternary;
      cursor: pointer;
    }

    &:last-of-type {
      margin-bottom: $margin-mini;
      border-radius: 0 0 8px 8px;
    }

    &:nth-child(odd) {
      background-color: $bg-quaternary;
    }
  }
}
</style>
